import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useChat as useTanstackChat } from '@tanstack/ai-react';

import { useUserDetails } from '@/features/_shared/user';
import type { ChatActions, ChatState } from '@/features/ai-chat/state/chat.context';
import { ChatActionsContext, ChatStateContext } from '@/features/ai-chat/state/chat.context';
import { addToDashboardToolDefinition } from '@/features/ai-chat/tools/addToDashboardTool';
import { displayTools } from '@/features/ai-chat/tools/displayTools';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';

import { useChatAnalytics } from '../../hooks/useChatAnalytics';
import { useChatHistorySync } from '../../hooks/useChatHistorySync';
import { useInitializeChat } from '../../hooks/useInitializeChat';
import { useUrlPromptIntegration } from '../../hooks/useUrlPromptIntegration';
import { fromPersistedMessage } from '../../lib/persistedMessage';
import { createTanstackConnection } from '../../lib/tanstackConnection';

import type { AnyClientTool, UIMessage } from '@tanstack/ai-client';
import type { Conversation, SendMessageArgs } from '../../types/chat.types';

export const ChatProvider = ({ open, children }: { open: boolean; children: React.ReactNode }) => {
  const { userEmail } = useUserDetails();
  const tripToken = useStorageValue(StorageKeys.TripToken);

  const [conversationId, setConversationId] = useState<string | null>('');
  const [animatedHumanId, setAnimatedHumanId] = useState<string | null>(null);
  const [animatedText, setAnimatedText] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [version, setVersion] = useState(0);
  const [ratings, setRatings] = useState<Record<string, -1 | 0 | 1>>({});
  const messagesRef = useRef<UIMessage[]>([]);

  const initChat = useInitializeChat();

  const saveMessageToHistory = useChatHistorySync(conversationId, messagesRef, ratings);
  const sendPosthogEvent = useChatAnalytics(conversationId, messagesRef);

  // `useChat()` reads `connection` only once, when it constructs the
  // underlying `ChatClient` (see node_modules/@tanstack/ai-react/src/use-chat.ts:
  // `const client = useMemo(() => { ... new ChatClient({ connection: ... }) ... },
  // [clientId])` — the factory closes over `optionsRef.current` but the memo
  // itself is keyed only on `clientId`, and none of the hook's per-option
  // `useEffect`s (body/forwardedProps/tools/context/queue/live) call
  // `client.updateOptions({ connection })`). So a `connection` rebuilt via
  // `useMemo(() => createTanstackConnection({...}), [conversationId, userEmail])`
  // would freeze at whatever `conversationId`/`userEmail` were on the very
  // first render (conversationId starts as `''`) for the component's entire
  // lifetime — every later send would hit the backend with an empty
  // `conversation_id` and `message`.
  //
  // Instead, build one stable connection over a mutable params object and
  // keep that object's fields current on every render/send.
  // `createTanstackConnection`'s generator reads `params.conversationId` /
  // `params.message` at *send* time, not at creation time (see
  // lib/tanstackConnection.ts), so mutating the same object in place is
  // observed correctly without ever needing to swap the connection itself.
  const connectionParamsRef = useRef<SendMessageArgs>({
    conversationId: '',
    message: '',
    userId: ''
  });
  connectionParamsRef.current.conversationId = conversationId ?? '';
  connectionParamsRef.current.userId = userEmail ?? '';

  const connection = useMemo(() => createTanstackConnection(connectionParamsRef.current), []);

  // `displayTools` (Task 1) are `ServerTool[]` — built via
  // `toolDefinition(...).server(...)` for TanStack's own server-side text
  // engine, which this bespoke backend doesn't use. `ServerTool` carries
  // `__toolSide: 'server'`, which is not part of `useChat()`'s
  // `tools: ReadonlyArray<AnyClientTool>` union (`ClientTool |
  // ToolDefinitionInstance`), so TS rejects passing it directly (TS2322).
  // The cast is safe: `ChatClient` only ever invokes a tool's `execute` from
  // its `onToolCall` handler, which fires exclusively off a CUSTOM chunk
  // named 'tool-input-available' (see
  // @tanstack/ai/src/activities/chat/stream/processor.ts, `handleCustomEvent`
  // around the `chunk.name === 'tool-input-available'` branch). Our
  // `createTanstackConnection` adapter (Task 3) never emits that chunk — only
  // TEXT_MESSAGE_*/TOOL_CALL_*/RUN_* chunks — so a `ServerTool`'s `execute`
  // can never be invoked through this path; `tools` here only needs to give
  // the client the name/schema metadata used to type/render `tool-call`
  // parts. Memoized so its identity is stable across renders (otherwise every
  // streamed chunk's re-render would recreate the array and trigger
  // `client.updateOptions({ tools })` for no reason).
  const tools = useMemo(
    () =>
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- documented safe cast, see comment block above
      [...displayTools, addToDashboardToolDefinition] as unknown as Array<AnyClientTool>,
    []
  );

  const {
    messages,
    sendMessage: sendTanstackMessage,
    isLoading,
    setMessages,
    addToolResult,
    addToolApprovalResponse
  } = useTanstackChat({
    connection,
    tools,
    onFinish: () => {
      saveMessageToHistory();
      sendPosthogEvent();
    }
  });
  messagesRef.current = messages;

  const handleSetConversationId = useCallback(
    (nextConversationId: string | null) => {
      if (nextConversationId === '') setMessages([]);
      setConversationId(nextConversationId);
    },
    [setMessages]
  );

  React.useEffect(() => {
    if (open && conversationId === '' && !initChat.isPending && tripToken) {
      initChat.mutate(undefined, {
        onSuccess: data => {
          setConversationId(data.conversation_id);
          setVersion(data.version ? Number(data.version) : 0);
        },
        onError: () => {
          setConversationId(null);
        }
      });
    }
  }, [open, conversationId, initChat, tripToken]);

  const sendMessage = useCallback(
    (text: string, _options?: { addHuman?: boolean }) => {
      if (!conversationId || !text.trim()) return;
      connectionParamsRef.current.message = text;
      void sendTanstackMessage(text);
    },
    [conversationId, sendTanstackMessage]
  );

  const handleSend = useCallback(() => {
    if (!inputMessage.trim() || animatedText) return;
    sendMessage(inputMessage);
    setInputMessage('');
  }, [inputMessage, sendMessage, animatedText]);

  // `useUrlPromptIntegration` (Task 8's file, unmodified here) still declares
  // `setMessages: Dispatch<SetStateAction<Message[]>>` and calls it with a
  // functional updater (`setMessages(prev => ...)`). The real `setMessages`
  // from `useTanstackChat()` is `(messages: UIMessage[]) => void` —
  // `ChatClient.setMessagesManually` (see
  // @tanstack/ai-react/src/use-chat.ts's `setMessagesManually` callback and
  // @tanstack/ai-client's `ChatClient.setMessagesManually`), which assigns
  // its argument directly and does not support a `(prev) => next` updater
  // form. Passing the real setter straight through both fails to typecheck
  // (`SetStateAction<T>`'s updater-function branch isn't assignable to a
  // plain `UIMessage[]` parameter) and would be wrong at runtime (the updater
  // function itself would be stored as "messages"). This adapter accepts
  // both forms and resolves an updater against the always-current
  // `messagesRef`, preserving `useUrlPromptIntegration`'s functional-update
  // usage without changing its file.
  const setMessagesCompat = useCallback<React.Dispatch<React.SetStateAction<UIMessage[]>>>(
    value => {
      const next = typeof value === 'function' ? value(messagesRef.current) : value;
      setMessages(next);
    },
    [setMessages]
  );

  useUrlPromptIntegration({
    conversationId,
    messagesRef,
    setMessages: setMessagesCompat,
    setAnimatedHumanId,
    setAnimatedText
  });

  const onAnimatedComplete = useCallback(() => {
    if (!animatedText) return;
    sendMessage(animatedText, { addHuman: false });
    setAnimatedHumanId(null);
    setAnimatedText(null);
  }, [animatedText, sendMessage]);

  const restoreConversation = useCallback(
    (conversation: Conversation) => {
      setMessages(conversation.messages.map(fromPersistedMessage));
      setRatings(
        Object.fromEntries(
          conversation.messages
            .filter(m => m.rating !== undefined)
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- `.filter` above isn't a type predicate, so TS can't narrow `rating` past `-1 | 0 | 1 | undefined`; the `!== undefined` check already excludes the only non-`-1|1` case in practice (persisted messages never store an explicit `0`).
            .map(m => [m.id, m.rating as -1 | 1])
        )
      );
      setConversationId(conversation.conversationId);
    },
    [setMessages]
  );

  const setRating = useCallback((id: string, score: -1 | 1) => {
    setRatings(prev => ({ ...prev, [id]: score }));
  }, []);

  const state = useMemo<ChatState>(
    () => ({
      messages,
      inputMessage,
      conversationId,
      version,
      animatedHumanId,
      animatedText,
      isInitializing: initChat.isPending,
      isSending: isLoading,
      ratings
    }),
    [
      messages,
      inputMessage,
      conversationId,
      version,
      animatedHumanId,
      animatedText,
      initChat.isPending,
      isLoading,
      ratings
    ]
  );

  const actions = useMemo<ChatActions>(
    () => ({
      setMessages,
      setInputMessage,
      handleSend,
      sendMessage,
      setConversationId: handleSetConversationId,
      restoreConversation,
      onAnimatedComplete,
      setRating,
      addToolApprovalResponse,
      addToolResult
    }),
    [
      setMessages,
      handleSend,
      sendMessage,
      handleSetConversationId,
      restoreConversation,
      onAnimatedComplete,
      setRating,
      addToolApprovalResponse,
      addToolResult
    ]
  );

  return (
    <ChatStateContext value={state}>
      <ChatActionsContext value={actions}>{children}</ChatActionsContext>
    </ChatStateContext>
  );
};
